import React, { useCallback, useEffect, useRef } from "react";

import { ArrowLeft, ArrowRight } from "lucide-react";

import { useNavigate } from "react-router-dom";

import useBanner from "../Services/useBanner";

const AUTO_SCROLL_DELAY = 2500;
const AUTO_SCROLL_RESUME_DELAY = 1800;

const DESKTOP_LOOP_REPEAT_COUNT = 18;
const DESKTOP_TOTAL_COPIES = 11;

const BannerSection = () => {
  const { banners, loading, fetchBanners } = useBanner();

  const navigate = useNavigate();

  const sliderRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const loopInitializedRef = useRef(false);
  const isLoopRepositioningRef = useRef(false);
  const autoScrollResumeTimeoutRef = useRef(null);
  const isTouchScrollingRef = useRef(false);
  const touchScrollEndTimeoutRef = useRef(null);
  const isTouchActiveRef = useRef(false);

  /* =====================================================
     FETCH BANNERS
  ===================================================== */

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  /* =====================================================
     GET URL FROM MEDIA STRING
  ===================================================== */

  const getBannerUrl = useCallback((mediaUrl, type) => {
    if (!mediaUrl) {
      return "";
    }

    const regex = new RegExp(`${type}:\\s*([^|]+)`, "i");

    return mediaUrl.match(regex)?.[1]?.trim() || "";
  }, []);

  /* =====================================================
     GET ONE CARD SCROLL WIDTH
  ===================================================== */

  const getSliderScrollAmount = useCallback(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return 0;
    }

    const firstCard = slider.firstElementChild;

    if (!firstCard) {
      return 0;
    }

    const cardWidth = firstCard.getBoundingClientRect().width;

    const sliderStyle = window.getComputedStyle(slider);

    const gap =
      Number.parseFloat(sliderStyle.columnGap || sliderStyle.gap || "0") || 0;

    return cardWidth + gap;
  }, []);

  /* =====================================================
     MOVE SLIDER
  ===================================================== */
const moveSlider = useCallback(
  (direction = "right") => {
    const slider = sliderRef.current;

    if (!slider || banners.length <= 1) {
      return;
    }

    const scrollAmount = getSliderScrollAmount();

    if (!scrollAmount) {
      return;
    }

    slider.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  },
  [banners.length, getSliderScrollAmount],
);

const normalizeLoopPosition = useCallback(() => {
  const slider = sliderRef.current;

  if (!slider || banners.length <= 1 || isLoopRepositioningRef.current) {
    return;
  }

  const scrollAmount = getSliderScrollAmount();

  if (!scrollAmount) {
    return;
  }

  const oneSetWidth = scrollAmount * banners.length;

  const currentPosition = slider.scrollLeft;

  const isDesktop = window.innerWidth >= 1024;

  // Mobile / tablet ka existing behaviour same.
  const repeatCount = isDesktop ? DESKTOP_LOOP_REPEAT_COUNT : 1;

  const shiftWidth = oneSetWidth * repeatCount;

  /*
   * Desktop:
   *
   * 1-2-3
   * 1-2-3
   * 1-2-3
   * 1-2-3
   * 1-2-3
   * 1-2-3
   * 1-2-3
   * 1-2-3
   *
   * then invisible reposition.
   */
  const leftResetPoint = oneSetWidth * 0.5;

  const rightResetPoint = oneSetWidth * (repeatCount + 1.5);

  let newPosition = null;

  // LEFT SIDE
if (currentPosition < leftResetPoint) {
  const targetPosition = currentPosition + shiftWidth;

  newPosition = isDesktop
    ? targetPosition
    : Math.round(targetPosition / scrollAmount) * scrollAmount;
} else if (currentPosition > rightResetPoint) {
  const targetPosition = currentPosition - shiftWidth;

  newPosition = isDesktop
    ? targetPosition
    : Math.round(targetPosition / scrollAmount) * scrollAmount;
}

  if (newPosition === null) {
    return;
  }

  isLoopRepositioningRef.current = true;

  // Invisible reposition
  slider.style.scrollBehavior = "auto";
  slider.style.scrollSnapType = "none";

  slider.scrollLeft = newPosition;

  requestAnimationFrame(() => {
    slider.style.scrollBehavior = "";
    slider.style.scrollSnapType = "";

    isLoopRepositioningRef.current = false;
  });
}, [banners.length, getSliderScrollAmount]);

useEffect(() => {
  const slider = sliderRef.current;

  if (loading || !slider || banners.length <= 1 || loopInitializedRef.current) {
    return;
  }

  const frame = requestAnimationFrame(() => {
    const scrollAmount = getSliderScrollAmount();

    if (!scrollAmount) {
      return;
    }

    const oneSetWidth = scrollAmount * banners.length;

    slider.style.scrollBehavior = "auto";
    slider.style.scrollSnapType = "none";

    slider.scrollLeft = oneSetWidth;

    requestAnimationFrame(() => {
      slider.style.scrollBehavior = "";
      slider.style.scrollSnapType = "";

      loopInitializedRef.current = true;
    });
  });

  return () => cancelAnimationFrame(frame);
}, [loading, banners.length, getSliderScrollAmount]);

  /* =====================================================
     AUTO SCROLL
  ===================================================== */

  /* =====================================================
   AUTO SCROLL
===================================================== */

  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    stopAutoScroll();

    if (banners.length <= 1) {
      return;
    }

    autoScrollIntervalRef.current = window.setInterval(() => {
      normalizeLoopPosition();

      requestAnimationFrame(() => {
        moveSlider("right");
      });
    }, AUTO_SCROLL_DELAY);
  }, [banners.length, moveSlider, normalizeLoopPosition, stopAutoScroll]);

  const restartAutoScroll = useCallback(() => {
    stopAutoScroll();

    if (autoScrollResumeTimeoutRef.current) {
      window.clearTimeout(autoScrollResumeTimeoutRef.current);
    }

    autoScrollResumeTimeoutRef.current = window.setTimeout(() => {
      startAutoScroll();
    }, AUTO_SCROLL_RESUME_DELAY);
  }, [startAutoScroll, stopAutoScroll]);

const handleLoopScroll = useCallback(() => {
  if (isTouchScrollingRef.current) {
    // Finger release ho chuka hai aur momentum abhi chal raha hai.
    if (!isTouchActiveRef.current) {
      if (touchScrollEndTimeoutRef.current) {
        window.clearTimeout(touchScrollEndTimeoutRef.current);
      }

      touchScrollEndTimeoutRef.current = window.setTimeout(() => {
        isTouchScrollingRef.current = false;

        normalizeLoopPosition();

        restartAutoScroll();

        touchScrollEndTimeoutRef.current = null;
      }, 150);
    }

    return;
  }

  normalizeLoopPosition();
}, [normalizeLoopPosition, restartAutoScroll]);

  /* =====================================================
   MOBILE TOUCH
===================================================== */

const handleTouchStart = useCallback(() => {
  isTouchActiveRef.current = true;
  isTouchScrollingRef.current = true;

  stopAutoScroll();

  if (autoScrollResumeTimeoutRef.current) {
    window.clearTimeout(autoScrollResumeTimeoutRef.current);

    autoScrollResumeTimeoutRef.current = null;
  }

  if (touchScrollEndTimeoutRef.current) {
    window.clearTimeout(touchScrollEndTimeoutRef.current);

    touchScrollEndTimeoutRef.current = null;
  }
}, [stopAutoScroll]);

const handleTouchEnd = useCallback(() => {
  isTouchActiveRef.current = false;

  if (touchScrollEndTimeoutRef.current) {
    window.clearTimeout(touchScrollEndTimeoutRef.current);
  }

  // Agar momentum scroll nahi aaya,
  // tab bhi 150ms baad settle ho jayega.
  touchScrollEndTimeoutRef.current = window.setTimeout(() => {
    isTouchScrollingRef.current = false;

    normalizeLoopPosition();

    restartAutoScroll();

    touchScrollEndTimeoutRef.current = null;
  }, 150);
}, [normalizeLoopPosition, restartAutoScroll]);

  /* =====================================================
   ARROW SCROLL
===================================================== */

const scrollSlider = (direction) => {
  normalizeLoopPosition();

  requestAnimationFrame(() => {
    moveSlider(direction);
  });

  restartAutoScroll();
};

  /* =====================================================
   START / CLEANUP AUTO SCROLL
===================================================== */

  useEffect(() => {
    if (loading || banners.length <= 1) {
      stopAutoScroll();

      return undefined;
    }

    startAutoScroll();

    return () => {
      stopAutoScroll();

      if (autoScrollResumeTimeoutRef.current) {
        window.clearTimeout(autoScrollResumeTimeoutRef.current);
      }
    };
  }, [loading, banners.length, startAutoScroll, stopAutoScroll]);

  /* =====================================================
     BANNER CLICK
  ===================================================== */

  const handleBannerClick = (banner) => {
    if (banner?.cta_type === "OFFERRESTROITEM" && banner?.cta_value) {
      navigate(`/offer-items/${banner.cta_value}`, {
        state: {
          title: banner.cta_text,
          selectedBanner: banner,
        },
      });
    }
  };

useEffect(() => {
  return () => {
    if (autoScrollResumeTimeoutRef.current) {
      window.clearTimeout(autoScrollResumeTimeoutRef.current);
    }

    if (touchScrollEndTimeoutRef.current) {
      window.clearTimeout(touchScrollEndTimeoutRef.current);
    }

    if (autoScrollIntervalRef.current) {
      window.clearInterval(autoScrollIntervalRef.current);
    }
  };
}, []);

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <>
        <style>
          {`
          @keyframes bannerShimmer {
            0% {
              transform: translateX(-120%);
            }

            100% {
              transform: translateX(120%);
            }
          }

          .banner-shimmer {
            animation: bannerShimmer 1.5s ease-in-out infinite;
          }
        `}
        </style>

        <section className="w-full bg-blue-50 py-5">
          <div className="mx-auto w-full max-w-[1350px] px-4 sm:px-5 md:px-8">
            {/* Optional top loading indicator */}
            <div className="mb-3 flex items-center gap-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />

              <div className="h-2.5 w-[75px] animate-pulse rounded-full bg-blue-100" />
            </div>

            {/* SKELETON BANNERS */}

            <div
              className="
              flex
              w-full
              gap-3
              overflow-hidden

              sm:gap-4
              lg:gap-5
            "
            >
              {[0, 1, 2].map((item) => (
                <div
                  key={item}
                  className="
                  relative

                  aspect-[16/7]

                  w-[88vw]
                  shrink-0
                  overflow-hidden

                  rounded-[17px]

                  border
                  border-blue-100/80

                  bg-gradient-to-br
                  from-[#e8eef7]
                  via-[#f1f5f9]
                  to-[#e5edf7]

                  shadow-sm

                  sm:w-[calc((100%-1rem)/2)]

                  lg:w-[calc((100%-2.5rem)/3)]
                "
                >
                  {/* SOFT BACKGROUND BLOBS */}

                  <div className="absolute -left-6 -top-8 h-28 w-28 rounded-full bg-white/35 blur-xl" />

                  <div className="absolute -bottom-10 right-3 h-32 w-32 rounded-full bg-blue-100/50 blur-xl" />

                  {/* MOVING SHIMMER */}

                  <div
                    className="
                    banner-shimmer

                    absolute
                    inset-y-0
                    left-0

                    w-[55%]

                    -skew-x-12

                    bg-gradient-to-r
                    from-transparent
                    via-white/75
                    to-transparent

                    blur-[1px]
                  "
                    style={{
                      animationDelay: `${item * 120}ms`,
                    }}
                  />

                  {/* FAKE BANNER CONTENT */}

                  <div className="absolute inset-0 flex items-end p-4 sm:p-5">
                    <div className="w-full">
                      <div
                        className="
                        h-3
                        w-[36%]
                        rounded-full
                        bg-white/55

                        sm:h-4
                      "
                      />

                      <div
                        className="
                        mt-2
                        h-2.5
                        w-[55%]
                        rounded-full
                        bg-white/40

                        sm:h-3
                      "
                      />

                      <div
                        className="
                        mt-3
                        h-7
                        w-[75px]
                        rounded-full
                        bg-white/55

                        sm:h-8
                        sm:w-[90px]
                      "
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!banners.length) {
    return null;
  }

  return (
    <section className="w-full bg-blue-50 pb-3 pt-5">
      <div className="mx-auto w-full max-w-[1350px] px-4 sm:px-5 md:px-8">
        {/* =================================================
            NAVIGATION ARROWS
        ================================================= */}

        {banners.length > 1 && (
          <div className="mb-2 hidden items-center justify-end gap-2 sm:flex"></div>
        )}

        {/* =================================================
            SLIDER
        ================================================= */}
        <div
          ref={sliderRef}
          onScroll={handleLoopScroll}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="
    flex
    w-full

    snap-x
    snap-mandatory

    gap-3

    overflow-x-auto
    overflow-y-hidden

    overscroll-x-contain
    touch-pan-x

    pb-2
    pt-1

    sm:gap-4
    lg:gap-5

    [-webkit-overflow-scrolling:touch]

    [scrollbar-width:none]
    [-ms-overflow-style:none]
    [&::-webkit-scrollbar]:hidden
  "
        >
          {Array.from({ length: DESKTOP_TOTAL_COPIES }, () => banners)
            .flat()
            .map((banner, index) => {
              const mobileUrl = getBannerUrl(banner.media_url, "mobile");

              const tabletUrl = getBannerUrl(banner.media_url, "tablet");

              const desktopUrl = getBannerUrl(banner.media_url, "desktop");

              /*
               * AFTER_FAST_DELIVERY banners
               * ka desired visual 16:7 hai.
               *
               * Isliye desktop par mobile
               * 16:7 asset ko priority de
               * rahe hain.
               */
              const bannerUrl = mobileUrl || tabletUrl || desktopUrl;

              return (
                <article
                  key={`${banner.pk_banner_id}-${index}`}
                  onClick={() => handleBannerClick(banner)}
                  className="
    group

   aspect-[16/7]

w-full
min-w-full
shrink-0

snap-start
snap-normal

    select-none
    cursor-pointer

    overflow-hidden
    rounded-[17px]

    bg-white
    shadow-sm

    transition
    duration-300

    sm:min-w-0
    sm:hover:-translate-y-0.5
    sm:hover:shadow-md

    sm:w-[calc((100%-1rem)/2)]
    lg:w-[calc((100%-2.5rem)/3)]
  "
                >
                  <img
                    src={bannerUrl}
                    alt={banner.banner_name || "Banner"}
                    loading="lazy"
                    draggable={false}
                    className="
    pointer-events-none
    block
    h-full
    w-full
    select-none
    object-cover
  "
                  />
                </article>
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default BannerSection;
